import React, { useEffect, useMemo, useState } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { TbExternalLink } from "react-icons/tb";

import { CustomerPickerField, SelectedCustomer } from "../../../../Components/CustomerPickerModal";
import { formatUserDisplayName } from "../../../../Helpers/Formatting/formatUserDisplayName";
import { TicketPropertyRow } from "./TicketPropertyRow";

interface UserFieldProps {
  userId: number | null;
  userName: string | null;
  onChange: (userId: number | null, userName?: string | null) => void;
  pending?: boolean;
  /** Open the customer-details flyout (the "View" button next to the customer). */
  onView?: () => void;
  locked?: boolean;
}

export function UserField({ userId, userName, onChange, pending, onView, locked }: UserFieldProps): React.ReactNode {
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);

  useEffect(() => {
    // Only reset when the parent provides a different userId (external change).
    // Skip reset when userId matches our own selection to preserve phone/locationName.
    if (selectedCustomer && selectedCustomer.id === userId) {
      return;
    }
    setSelectedCustomer(null);
  }, [userId]);

  const pickerCustomer = useMemo(() => {
    if (selectedCustomer) {
      return selectedCustomer;
    }

    if (userId) {
      return { id: userId, name: formatUserDisplayName(userId, userName) };
    }

    return null;
  }, [selectedCustomer, userId, userName]);

  const displayName = useMemo(() => {
    return pickerCustomer ? formatUserDisplayName(pickerCustomer.id, pickerCustomer.name) : null;
  }, [pickerCustomer]);

  const handleSelect = (customer: SelectedCustomer): void => {
    setSelectedCustomer(customer);
    onChange(customer.id, customer.name);
  };

  if (locked) {
    return (
      <TicketPropertyRow label="Customer" lockedAction="Updating customers">
        {displayName ?? "No customer"}
      </TicketPropertyRow>
    );
  }

  return (
    <TicketPropertyRow label="Customer" pending={pending}>
      <CustomerPickerField selectedCustomer={pickerCustomer} onSelect={handleSelect} onRemove={() => onChange(null, null)}>
        {({ onClick }) =>
          userId ? (
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClick} className="flex cursor-pointer items-center rounded px-2 py-0.5 text-sm hover:bg-grey-50">
                <span className="font-semibold text-black">{displayName}</span>
                <IoIosArrowDown className="ml-2 h-3.5 w-3.5 text-grey-600" />
              </button>
              {onView && (
                <button
                  type="button"
                  onClick={onView}
                  className="flex shrink-0 cursor-pointer items-center gap-0.5 rounded-full bg-grey-50 px-1.5 py-0.5 text-black transition hover:bg-grey-100"
                >
                  <TbExternalLink className="h-4 w-4" />
                  <span className="body-xs">View</span>
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={onClick}
              className="flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 text-sm text-grey-600 hover:bg-grey-50"
            >
              No customer
              <IoIosArrowDown className="h-3.5 w-3.5 text-grey-600" />
            </button>
          )
        }
      </CustomerPickerField>
    </TicketPropertyRow>
  );
}
